import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import NavBar from '@/components/NavBar';
import Footer from '@/components/Footer';
import { Display } from '@/types/sequence';
import { useDisplays, convertToDisplayWithOwner } from '@/hooks/useDisplays';
import DisplaySearchBar from '@/components/displays/DisplaySearchBar';
import DisplayCategories from '@/components/displays/DisplayCategories';
import { DisplayWithOwner } from '@/data/mockDisplaysData';
import { AdvancedSearchService, SearchFilters } from '@/lib/advancedSearch';
import { LocationService } from '@/lib/locationService';

// Mock display categories
const categories = [
  { id: 'all', name: 'All Displays' },
  { id: 'drive-through', name: 'Drive Through Displays' },
  { id: 'static', name: 'Static Displays' },
  { id: 'animated', name: 'Animated Displays' },
  { id: 'road-neighborhood', name: 'Road/Neighborhood' },
  { id: 'commercial-paid', name: 'Commercial/Paid Displays' },
  { id: 'christmas', name: 'Christmas' },
  { id: 'halloween', name: 'Halloween' },
  { id: 'musical', name: 'Musical' },
  { id: 'mega', name: 'Mega Displays' },
];

const Displays = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialQuery = searchParams.get('q') || '';
  
  const [activeCategory, setActiveCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState(initialQuery);
  const [advancedResults, setAdvancedResults] = useState<any[]>([]);
  const [isAdvancedSearchLoading, setIsAdvancedSearchLoading] = useState(false);
  const [useAdvancedSearch, setUseAdvancedSearch] = useState(false);
  
  const { data: displays = [], isLoading, error } = useDisplays();

  // Check if we should use advanced search based on URL parameters
  useEffect(() => {
    const city = searchParams.get('city');
    const state = searchParams.get('state');
    const holiday = searchParams.get('holiday');
    const type = searchParams.get('type');
    const zip = searchParams.get('zip');

    // Use advanced search if we have geographic or specific filter parameters
    const shouldUseAdvanced = !!(city || state || holiday || type || zip);
    setUseAdvancedSearch(shouldUseAdvanced);

    if (shouldUseAdvanced) {
      performAdvancedSearch();
    }
  }, [searchParams]);

  // Update search query when URL params change
  useEffect(() => {
    setSearchQuery(initialQuery);
  }, [initialQuery]);

  // Perform advanced search with geographic filtering
  const performAdvancedSearch = async () => {
    setIsAdvancedSearchLoading(true);
    try {
      const city = searchParams.get('city');
      const state = searchParams.get('state');
      const holiday = searchParams.get('holiday');
      const type = searchParams.get('type');
      const zip = searchParams.get('zip');
      const query = searchParams.get('q');

      // Build search filters
      const filters: SearchFilters = {
        limit: 100,
        sortBy: 'relevance'
      };

      // Add text query if present
      if (query) {
        filters.query = query;
      }

      // Add location-based filtering
      if (city || state || zip) {
        let locationText = '';
        if (city && state) {
          locationText = `${city}, ${state}`;
        } else if (city) {
          locationText = city;
        } else if (state) {
          locationText = state;
        } else if (zip) {
          locationText = zip;
        }
        
        if (locationText) {
          filters.locationText = locationText;
          
          // Try to geocode for radius-based search
          try {
            const geocoded = await LocationService.geocodeAddress(locationText);
            if (geocoded) {
              filters.location = {
                latitude: geocoded.latitude,
                longitude: geocoded.longitude
              };
              filters.radius = 50000; // 50km radius
            }
          } catch (error) {
            console.warn('Failed to geocode location:', locationText);
          }
        }
      }

      // Add holiday filtering
      if (holiday) {
        const holidayMap: Record<string, string> = {
          'christmas': 'Christmas',
          'halloween': 'Halloween',
          'musical': 'LightShow', // This maps to display_type
          'drive-thru': 'Drive-Through'
        };
        
        if (holiday === 'musical') {
          filters.displayTypes = ['LightShow'];
        } else if (holidayMap[holiday]) {
          filters.holidayTypes = [holidayMap[holiday]];
        }
      }

      // Add type filtering  
      if (type) {
        const typeMap: Record<string, string> = {
          'musical': 'LightShow',
          'drive-thru': 'Drive-Through',
          'synchronized': 'LightShow',
          'family': 'Residential'
        };
        
        if (typeMap[type]) {
          if (!filters.displayTypes) filters.displayTypes = [];
          filters.displayTypes.push(typeMap[type]);
        }
      }

      console.log('Advanced search filters:', filters);
      
      // Perform the search
      const searchResponse = await AdvancedSearchService.search(filters);
      
      // Transform results to match our display format
      const transformedResults = searchResponse.results.map((result: any) => ({
        id: result.id,
        name: result.title,
        location: result.location || '',
        description: result.description || '',
        image_url: result.imageUrl || '',
        latitude: result.latitude || 0,
        longitude: result.longitude || 0,
        display_type: result.displayType || '',
        holidays: result.holidayType ? [result.holidayType] : [],
        rating: result.rating || 0,
        year_started: result.yearStarted || null,
        tags: [],
        // Add required fields that might be missing
        schedule: '',
        owner: null
      }));

      setAdvancedResults(transformedResults);
    } catch (error) {
      console.error('Advanced search failed:', error);
      // Fall back to basic search if advanced search fails
      setUseAdvancedSearch(false);
    } finally {
      setIsAdvancedSearchLoading(false);
    }
  };

  // Filter displays based on search and category (fallback to basic filtering)
  const getFilteredDisplays = () => {
    if (useAdvancedSearch) {
      return advancedResults;
    }

    return displays
      .filter(display => {
        // Category filter
        if (activeCategory === 'all') return true;
        if (activeCategory === 'drive-through') {
          return (
            display.tags?.some(tag => tag.toLowerCase().includes('drive')) ||
            (display.description && display.description.toLowerCase().includes('drive through'))
          );
        }
        if (activeCategory === 'static') {
          return !(
            (display.display_type &&
              (display.display_type.toLowerCase().includes('musical') ||
               display.display_type.toLowerCase().includes('animated') ||
               display.display_type.toLowerCase().includes('lightshow'))
          ) &&
            !display.tags?.some(tag => ['musical', 'animated', 'lightshow'].includes(tag.toLowerCase()))
          );
        }
        if (activeCategory === 'animated') {
          return (
            (display.display_type && display.display_type.toLowerCase().includes('animated')) ||
            display.tags?.some(tag => tag.toLowerCase().includes('animated'))
          );
        }
        if (activeCategory === 'road-neighborhood') {
          return (
            (display.display_type && display.display_type.toLowerCase().includes('residential')) ||
            display.tags?.some(tag => tag.toLowerCase().includes('neighborhood') || tag.toLowerCase().includes('road')) ||
            (display.description && (display.description.toLowerCase().includes('neighborhood') || display.description.toLowerCase().includes('road')))
          );
        }
        if (activeCategory === 'commercial-paid') {
          return (
            (display.display_type && display.display_type.toLowerCase().includes('commercial')) ||
            display.tags?.some(tag => tag.toLowerCase().includes('commercial') || tag.toLowerCase().includes('paid')) ||
            (display.description && (display.description.toLowerCase().includes('commercial') || display.description.toLowerCase().includes('paid')))
          );
        }
        if (activeCategory === 'christmas' && display.holidays && display.holidays.includes('Christmas')) return true;
        if (activeCategory === 'halloween' && display.holidays && display.holidays.includes('Halloween')) return true;
        if (activeCategory === 'musical' && display.display_type === 'LightShow') return true;
        if (activeCategory === 'mega' && display.tags?.includes('Mega Display')) return true;
        return false;
      })
      .filter(display =>
        searchQuery === '' ||
        display.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        display.location.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (display.description && display.description.toLowerCase().includes(searchQuery.toLowerCase()))
      );
  };

  const filteredDisplays = getFilteredDisplays();

  // Convert to DisplayWithOwner format for rendering
  const displayItems: DisplayWithOwner[] = filteredDisplays.map(display => 
    convertToDisplayWithOwner(display) as DisplayWithOwner
  );

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const newParams = new URLSearchParams(searchParams);
    if (searchQuery) {
      newParams.set('q', searchQuery);
    } else {
      newParams.delete('q');
    }
    setSearchParams(newParams);
  };

  // Determine loading state
  const isDisplaysLoading = useAdvancedSearch ? isAdvancedSearchLoading : isLoading;
  
  // Generate page description based on search parameters
  const getPageDescription = () => {
    const city = searchParams.get('city');
    const state = searchParams.get('state');
    const holiday = searchParams.get('holiday');
    
    if (city && state && holiday) {
      return `Find ${holiday} light displays in ${city}, ${state}. Browse spectacular holiday light shows, get directions, and read reviews.`;
    } else if (city && state) {
      return `Discover holiday light displays in ${city}, ${state}. Find the best Christmas and Halloween light shows near you.`;
    } else if (state && holiday) {
      return `Browse ${holiday} light displays throughout ${state}. Find amazing holiday light shows and displays.`;
    }
    
    return "Explore holiday light displays around the world. Find magical experiences near you or plan your next light-hunting adventure.";
  };

  // Prefetch common searches on component mount
  useEffect(() => {
    // Prefetch popular searches for better performance
    const prefetchTimer = setTimeout(() => {
      AdvancedSearchService.prefetchCommonSearches();
    }, 2000); // Delay to not impact initial page load

    return () => clearTimeout(prefetchTimer);
  }, []);

  return (
    <div className="min-h-screen flex flex-col">
      <NavBar />
      
      <main className="flex-1 pt-24 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          {/* Header */}
          <div className="mb-10">
            <h1 className="text-4xl font-bold mb-2">
              {(() => {
                const city = searchParams.get('city');
                const state = searchParams.get('state');
                const holiday = searchParams.get('holiday');
                
                if (city && state && holiday) {
                  return `${holiday.charAt(0).toUpperCase() + holiday.slice(1)} Light Displays in ${city}, ${state}`;
                } else if (city && state) {
                  return `Holiday Light Displays in ${city}, ${state}`;
                } else if (state && holiday) {
                  return `${holiday.charAt(0).toUpperCase() + holiday.slice(1)} Displays in ${state}`;
                }
                
                return "Discover Light Displays";
              })()}
            </h1>
            <p className="text-muted-foreground max-w-3xl">
              {getPageDescription()}
            </p>
            
            {/* Show search info if using advanced search */}
            {useAdvancedSearch && (
              <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm text-blue-800">
                  {(() => {
                    const city = searchParams.get('city');
                    const state = searchParams.get('state');
                    const holiday = searchParams.get('holiday');
                    
                    let parts = [];
                    if (city) parts.push(`in ${city}`);
                    if (state && !city) parts.push(`in ${state}`);
                    if (holiday) parts.push(`for ${holiday}`);
                    
                    const locationPart = parts.join(' ');
                    const resultCount = filteredDisplays.length;
                    
                    return `Found ${resultCount} display${resultCount !== 1 ? 's' : ''} ${locationPart} using geographic search`;
                  })()}
                </p>
              </div>
            )}
          </div>
          
          <DisplaySearchBar 
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            handleSearch={handleSearch}
          />
          
          {isDisplaysLoading ? (
            <div className="text-center py-12">
              <h3 className="text-xl font-medium mb-2">
                {useAdvancedSearch ? 'Searching displays...' : 'Loading displays...'}
              </h3>
            </div>
          ) : error ? (
            <div className="text-center py-12 text-destructive">
              <h3 className="text-xl font-medium mb-2">Error loading displays</h3>
              <p>{(error as Error).message}</p>
            </div>
          ) : (
            <DisplayCategories 
              categories={categories}
              activeCategory={activeCategory}
              setActiveCategory={setActiveCategory}
              displayItems={displayItems}
            />
          )}
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default Displays;
